import { isNil } from 'es-toolkit';
import { DateTime } from 'luxon';

import type {
  AccountCustodyName,
  PortfolioItemTypeName,
  ProviderAccountSource,
} from '@savvy/constants/generated/gql-enums';
import type { AccountSubtypeName } from '@savvy/constants/generated/gql-types';

import { isPresent } from '../common/utils';
import {
  ACCOUNT_DETAIL_CHANGE_THROUGH_PREFIX,
  ACCOUNT_DETAIL_MANAGED_BY_HOUSEHOLD,
  ACCOUNT_DETAIL_MANAGED_BY_SAVVY,
  ACCOUNT_DETAIL_SELF_MANAGED,
  ACCOUNT_DETAIL_SYNCED_PREFIX,
  ACCOUNT_DETAIL_UNKNOWN_TYPE,
  ACCOUNT_RETURN_TOOLTIP_NOTE,
} from './constants';
import { accountCustomName, accountTypeLabel, isManagedBySavvy, signedAccountAmount } from './utils';

export type AccountDetailInput = {
  readonly id: string;
  readonly accountName: string;
  readonly custody: AccountCustodyName;
  readonly customName?: string | null;
  readonly displayBalance: { readonly amount: string };
  readonly firmName?: string | null;
  readonly lastUpdatedAt?: string | null;
  readonly mask?: string | null;
  readonly providerAccount?: {
    readonly id: string;
    readonly providerName?: string | null;
    readonly source: ProviderAccountSource;
  } | null;
  readonly providerLogoUrl?: string | null;
  readonly subtype?: AccountSubtypeName | null;
  readonly type: PortfolioItemTypeName;
};

export function accountDetailBalance(account: AccountDetailInput) {
  return signedAccountAmount(account.displayBalance.amount, account.type);
}

export function accountBalanceEditable(account: AccountDetailInput) {
  return !isManagedBySavvy(account.custody) && isNil(account.providerAccount);
}

export function accountManagementLabel(custody: AccountCustodyName) {
  if (isManagedBySavvy(custody)) {
    return ACCOUNT_DETAIL_MANAGED_BY_SAVVY;
  }
  return custody === 'held_away' ? ACCOUNT_DETAIL_MANAGED_BY_HOUSEHOLD : ACCOUNT_DETAIL_SELF_MANAGED;
}

export function accountCustodian(account: AccountDetailInput) {
  if (isPresent(account.providerAccount?.providerName)) {
    return account.providerAccount.providerName;
  }
  return isPresent(account.firmName) ? account.firmName : null;
}

export function accountDetailTypeLabel(account: AccountDetailInput) {
  return accountTypeLabel(account) ?? ACCOUNT_DETAIL_UNKNOWN_TYPE;
}

export function lastUpdatedLabel(isoDateTime: string | null | undefined, now: DateTime = DateTime.now()) {
  if (!isPresent(isoDateTime)) {
    return null;
  }
  const parsed = DateTime.fromISO(isoDateTime);
  return parsed.isValid ? parsed.toRelative({ base: now }) : null;
}

export function accountDetailSubLine(account: AccountDetailInput, now: DateTime = DateTime.now()) {
  const relative = lastUpdatedLabel(account.lastUpdatedAt, now);
  const parts = [
    accountCustomName(account) === null ? null : account.accountName,
    accountDetailTypeLabel(account),
    isPresent(account.mask) ? `···${account.mask}` : null,
    isPresent(relative) ? `${ACCOUNT_DETAIL_SYNCED_PREFIX} ${relative}` : null,
  ].filter(isPresent);
  return parts.join(' · ');
}

export type AccountSyncTarget = {
  readonly providerAccountId: string;
  readonly via: 'providerAccount' | 'moneykit';
};

export function accountSyncTarget(account: AccountDetailInput): AccountSyncTarget | null {
  const providerAccount = account.providerAccount;
  if (isNil(providerAccount)) {
    return null;
  }
  return {
    providerAccountId: providerAccount.id,
    via: providerAccount.source === 'MONEYKIT' ? 'moneykit' : 'providerAccount',
  };
}

export function accountReturnNote(endDate: string | null | undefined, template = ACCOUNT_RETURN_TOOLTIP_NOTE) {
  const parsed = isPresent(endDate) ? DateTime.fromISO(endDate) : null;
  const through =
    parsed?.isValid === true ? ` ${ACCOUNT_DETAIL_CHANGE_THROUGH_PREFIX} ${parsed.toFormat('LLL d')}` : '';
  return template.replace('{through}', through);
}

export function accountChangeCaption(rangeCaption: string, seriesEndDate: string | undefined) {
  if (!isPresent(seriesEndDate)) {
    return rangeCaption;
  }
  const parsed = DateTime.fromISO(seriesEndDate);
  if (!parsed.isValid) {
    return rangeCaption;
  }
  return `${rangeCaption} ${ACCOUNT_DETAIL_CHANGE_THROUGH_PREFIX} ${parsed.toFormat('LLL d')}`;
}
